/**
 * @file: MaintenancePlanManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  CachedTaskDetail,
  MaintenancePlanInfo,
  SaveMaintenancePlanDetailPayload,
  TaskDetailVersions,
  VersionedResponse,
} from '@euler/model/task-detail';
import { WritableDraft } from 'immer/dist/internal';
import { TaskWorkflowNodeManager } from '../TaskWorkflowNodeManager';
import { ITaskManager } from '../types';

export class MaintenancePlanManager extends TaskWorkflowNodeManager {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(taskManager: ITaskManager) {
    super(taskManager);
  }

  async saveMaintenancePlanDetail(payload: SaveMaintenancePlanDetailPayload) {
    const resp = await this.taskService.saveMaintenancePlanDetail(
      this.taskNo,
      payload,
    );
    this.handleVersionedResponse(resp, (_, { maintenancePlan }) => {
      if (maintenancePlan.plan == null) {
        maintenancePlan.plan = resp.response;
      } else {
        const detail = resp.response.details[0];
        const index = maintenancePlan.plan.details.findIndex(
          x => x.name === detail.name,
        );
        if (index >= 0) {
          maintenancePlan.plan.details[index] = detail;
        } else {
          maintenancePlan.plan.details.push(detail);
        }
      }
    });
  }

  async deleteMaintenancePlanDetail(id: number) {
    const resp = await this.taskService.deleteMaintenancePlanDetail(
      this.taskNo,
      id,
    );
    this.handleVersionedResponse(resp, (_, { maintenancePlan }) => {
      if (maintenancePlan.plan == null) {
        console.warn('maintenancePlan.plan is null, this must be a bug');
        return;
      }
      const index = maintenancePlan.plan.details.findIndex(x => x.id === id);
      if (index < 0) {
        console.warn(
          `cannot find maintenance plan detail #${id} , this must be a bug`,
        );
        return;
      }
      maintenancePlan.plan.details.splice(index, 1);
      if (maintenancePlan.plan.details.length === 0) {
        maintenancePlan.plan = undefined;
      }
    });
  }

  private handleVersionedResponse<U>(
    versionedResponse: VersionedResponse<U>,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        response: U;
        maintenancePlan: MaintenancePlanInfo;
        version: number;
        original: MaintenancePlanInfo;
      },
    ) => void,
  ) {
    return this.produceWithVersions(
      versionedResponse.versions,
      (draft, context) => {
        recipe(draft, { ...context, response: versionedResponse.response });
      },
    );
  }

  private produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        maintenancePlan: MaintenancePlanInfo;
        version: number;
        original: MaintenancePlanInfo;
      },
    ) => void,
  ) {
    const original = this.cachedDetail.maintenancePlan;
    const version = versions.maintenancePlan;

    if (
      version === original.version &&
      this.cachedDetail.version === versions.detail
    ) {
      // nothing changed
      return this.cachedDetail;
    }

    return this.taskManager.produceWithVersions(versions, draft => {
      const { maintenancePlan } = draft;
      recipe(draft, {
        maintenancePlan,
        version,
        original,
      });
      if (maintenancePlan.version !== version) {
        maintenancePlan.version = version;
      }
    });
  }
}
